(() => {
  'use strict';

  const videoSelector = '[data-advertorial-video-player]';
  const mobileQuery = window.matchMedia('(max-width: 599px)');
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const saveDataEnabled = () => Boolean(connection && connection.saveData);

  const pauseOtherVideos = (currentVideo) => {
    document.querySelectorAll(`${videoSelector} [data-advertorial-video]`).forEach((video) => {
      if (video === currentVideo) return;
      video.defaultMuted = true;
      video.muted = true;
      if (!video.paused) video.pause();
    });
  };

  const initialiseVideo = (player) => {
    if (player.dataset.advertorialVideoReady === 'true') return;

    const video = player.querySelector('[data-advertorial-video]');
    const action = player.querySelector('[data-advertorial-video-action]');
    const actionLabel = player.querySelector('[data-advertorial-video-action-label]');
    if (!video || !action || !actionLabel) return;

    let userActivated = false;
    let autoplayFailed = false;
    let isVisible = false;

    video.defaultMuted = true;
    video.muted = true;

    const autoplayIsAllowed = () => (
      mobileQuery.matches
      && !reducedMotionQuery.matches
      && !saveDataEnabled()
      && 'IntersectionObserver' in window
    );

    const updateAction = () => {
      const hasSound = !video.muted && video.volume > 0;
      const shouldHide = !video.paused && hasSound;

      if (shouldHide) {
        const actionHadFocus = document.activeElement === action;
        action.hidden = true;
        if (actionHadFocus) video.focus({ preventScroll: true });
        return;
      }

      action.hidden = false;
      if (video.ended) {
        actionLabel.textContent = 'Replay video';
        action.setAttribute('aria-label', 'Replay video with sound');
      } else if (video.paused || autoplayFailed) {
        actionLabel.textContent = 'Play video';
        action.setAttribute('aria-label', 'Play video with sound');
      } else {
        actionLabel.textContent = 'Tap for sound';
        action.setAttribute('aria-label', 'Turn on video sound');
      }
    };

    const playMuted = () => {
      if (userActivated || !isVisible || !autoplayIsAllowed() || video.ended) {
        updateAction();
        return;
      }

      video.defaultMuted = true;
      video.muted = true;
      pauseOtherVideos(video);
      const playAttempt = video.play();
      if (playAttempt && typeof playAttempt.catch === 'function') {
        playAttempt
          .then(() => {
            autoplayFailed = false;
            updateAction();
          })
          .catch(() => {
            autoplayFailed = true;
            updateAction();
          });
      }
    };

    action.addEventListener('click', () => {
      userActivated = true;
      autoplayFailed = false;
      pauseOtherVideos(video);
      video.defaultMuted = false;
      video.muted = false;
      if (video.ended) video.currentTime = 0;

      const playAttempt = video.play();
      if (playAttempt && typeof playAttempt.catch === 'function') {
        playAttempt.catch(() => {
          autoplayFailed = true;
          video.defaultMuted = true;
          video.muted = true;
          updateAction();
        });
      }
      updateAction();
    });

    ['play', 'playing', 'pause', 'ended', 'volumechange'].forEach((eventName) => {
      video.addEventListener(eventName, () => {
        if (eventName === 'play') pauseOtherVideos(video);
        if (eventName === 'playing') autoplayFailed = false;
        updateAction();
      });
    });

    const enforceAutoplayPolicy = () => {
      if (!autoplayIsAllowed()) {
        if (!userActivated && !video.paused) video.pause();
        video.defaultMuted = true;
        video.muted = true;
        updateAction();
        return;
      }

      if (isVisible) playMuted();
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          isVisible = entry.intersectionRatio >= 0.55;
          if (isVisible) {
            playMuted();
          } else if (!video.paused) {
            video.pause();
            video.defaultMuted = true;
            video.muted = true;
          }
        });
      }, { threshold: [0, 0.55] });
      observer.observe(video);
    }

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) return;
      video.defaultMuted = true;
      video.muted = true;
      if (!video.paused) video.pause();
    });

    [mobileQuery, reducedMotionQuery].forEach((query) => {
      if (typeof query.addEventListener === 'function') {
        query.addEventListener('change', enforceAutoplayPolicy);
      }
    });
    if (connection && typeof connection.addEventListener === 'function') {
      connection.addEventListener('change', enforceAutoplayPolicy);
    }

    player.dataset.advertorialVideoReady = 'true';
    action.hidden = false;
    updateAction();
  };

  const initialiseStickyCta = (root) => {
    if (!root || root.dataset.advertorialStickyReady === 'true') return;

    const cta = root.querySelector('[data-advertorial-sticky-cta]');
    const hero = root.querySelector('.codex-advertorial__hero-media');
    const stop = root.querySelector('[data-advertorial-sticky-stop]');
    if (!cta || !hero || !stop) return;

    let ticking = false;

    const update = () => {
      ticking = false;
      const heroHasPassed = hero.getBoundingClientRect().bottom <= 0;
      const stopIsVisible = stop.getBoundingClientRect().top <= window.innerHeight;
      const shouldShow = mobileQuery.matches && heroHasPassed && !stopIsVisible;

      cta.classList.toggle('is-visible', shouldShow);
      cta.setAttribute('aria-hidden', String(!shouldShow));
      cta.toggleAttribute('inert', !shouldShow);
    };

    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', requestUpdate);
    }

    root.dataset.advertorialStickyReady = 'true';
    update();
  };

  const initialiseAdvertorial = (scope = document) => {
    scope.querySelectorAll(videoSelector).forEach(initialiseVideo);
    const root = scope.matches && scope.matches('[data-advertorial-story]')
      ? scope
      : scope.querySelector('[data-advertorial-story]');
    initialiseStickyCta(root);
  };

  initialiseAdvertorial();
  document.addEventListener('shopify:section:load', (event) => initialiseAdvertorial(event.target));
})();
